<script setup lang="ts">
import { computed, ref } from 'vue';
// <altcha-widget> is registered once, in `main.ts` — see the note there.
import BrandLogo from './BrandLogo.vue';
import PasswordField from './PasswordField.vue';
import { ApiError, api } from '@/net/api';

/**
 * What `/reset-password?token=…` opens.
 *
 * The other half of the pair `state/deep-link.ts` describes, and it had the same
 * problem: core's `forgotPassword` emails a link built from `APP_URL` and this app
 * had nothing behind that path, so the reset flow existed end to end on the server
 * and was unreachable from the browser.
 *
 * **Confirmation is checked here and not sent.** Its whole job is catching a typo
 * in a field the typist cannot read, which is a property of this form rather than
 * of the account — the server has nothing to do with a third string except compare
 * two values the same caller handed it. `ChangePasswordDto` makes the same
 * argument for the signed-in version.
 *
 * **Success does not sign anybody in**, deliberately. Core's `resetPassword`
 * revokes every refresh token for the account, which is the point of a reset;
 * issuing a session on the strength of a link from an inbox would undo half of it.
 * The player signs in with the password they just chose, which also confirms it
 * took.
 *
 * Same background, palette and `landing.css` import as `JoinScreen.vue` — a
 * player who followed an email link here should land on the same screen the
 * game otherwise opens on, not a plainer one that happens to share a URL.
 */
const emit = defineEmits<{ done: [] }>();
const props = defineProps<{ token: string }>();

const PASSWORD_MIN = 8;

// Same dedicated backdrop pair as `JoinScreen.vue`, picked the same way —
// see that file's note.
const BACKDROPS = [
  {
    image: '/assets/auth/backdrop.webp',
    webm: '/assets/auth/backdrop.webm',
    mp4: '/assets/auth/backdrop.mp4',
  },
  {
    image: '/assets/auth/backdrop2.webp',
    webm: '/assets/auth/backdrop2.webm',
    mp4: '/assets/auth/backdrop2.mp4',
  },
];
const BACKDROP = BACKDROPS[Math.floor(Math.random() * BACKDROPS.length)];

// See `JoinScreen.vue`'s identical constant — `hidefooter`/`hidelogo` as bare
// attributes are not recognised by this build of the widget.
const ALTCHA_CONFIG = JSON.stringify({ hideFooter: true, hideLogo: false });
const backdropVideoReady = ref(false);
const backdropReducedMotion =
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

const password = ref('');
const confirm = ref('');
const altchaPayload = ref('');
const altchaEl = ref<(HTMLElement & { reset?: () => void; verify?: () => void }) | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);
const done = ref(false);

const mismatch = computed(
  () => confirm.value.length > 0 && password.value !== confirm.value,
);

const canSubmit = computed(
  () =>
    !busy.value &&
    altchaPayload.value !== '' &&
    password.value.length >= PASSWORD_MIN &&
    password.value === confirm.value,
);

function onAltchaState(event: Event): void {
  const detail = (event as CustomEvent<{ state?: string; payload?: string }>).detail;
  altchaPayload.value = detail?.state === 'verified' ? (detail.payload ?? '') : '';
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  busy.value = true;
  error.value = null;
  try {
    await api.resetPassword(props.token, password.value, altchaPayload.value);
    done.value = true;
  } catch (err) {
    error.value =
      err instanceof ApiError
        ? err.message
        : 'Could not reach the server. Try again in a moment.';
    // A spent proof-of-work cannot be reused, so the checkbox goes back to
    // unverified and waits for another click — the same reset `JoinScreen` does.
    altchaPayload.value = '';
    altchaEl.value?.reset?.();
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <div class="landing screen" data-test="reset-screen" :style="{ backgroundImage: `url(${BACKDROP.image})` }">
    <video
      v-if="!backdropReducedMotion"
      class="backdrop-video"
      :class="{ ready: backdropVideoReady }"
      autoplay
      muted
      loop
      playsinline
      preload="auto"
      @playing="backdropVideoReady = true"
    >
      <source :src="BACKDROP.webm" type="video/webm" />
      <source :src="BACKDROP.mp4" type="video/mp4" />
    </video>
    <div class="scrim" aria-hidden="true"></div>

    <div class="panel" :class="{ locked: busy }">
      <span class="bracket bracket-tl" aria-hidden="true"></span>
      <span class="bracket bracket-tr" aria-hidden="true"></span>
      <span class="bracket bracket-bl" aria-hidden="true"></span>
      <span class="bracket bracket-br" aria-hidden="true"></span>

      <BrandLogo class="panel-logo" aria-hidden="true" />
      <span class="sr-only">Steel of War</span>

      <template v-if="done">
        <p class="tagline">Password changed</p>
        <h2 data-test="reset-ok">Every device signed out</h2>
        <p class="lede">Sign in with the new password.</p>
        <button type="button" class="submit live" data-test="reset-continue" @click="emit('done')">
          Sign in
        </button>
      </template>

      <template v-else>
        <p class="tagline">Join the front</p>
        <h2>Choose a new password</h2>
        <p class="lede">
          This link works once. Setting a password signs out every device.
        </p>

        <form @submit.prevent="submit">
          <PasswordField
            id="new-password"
            v-model="password"
            label="New password"
            autocomplete="new-password"
            data-test="reset-password"
            :placeholder="`At least ${PASSWORD_MIN} characters`"
            :disabled="busy"
          />

          <PasswordField
            id="confirm-password"
            v-model="confirm"
            label="Repeat it"
            autocomplete="new-password"
            data-test="reset-confirm"
            :disabled="busy"
          />
          <p v-if="mismatch" class="mismatch" data-test="reset-mismatch">
            Those two do not match.
          </p>

          <altcha-widget
            ref="altchaEl"
            class="altcha"
            challenge="/api/auth/altcha-challenge"
            name="altcha"
            :configuration="ALTCHA_CONFIG"
            data-test="altcha"
            @statechange="onAltchaState"
          ></altcha-widget>

          <button
            type="submit"
            class="submit"
            :class="{ live: canSubmit }"
            data-test="reset-submit"
            :disabled="!canSubmit"
          >
            {{ busy ? 'Working…' : 'Set the password' }}
          </button>
        </form>

        <p v-if="error" class="error" data-test="reset-error">{{ error }}</p>

        <button type="button" class="back" data-test="reset-cancel" @click="emit('done')">
          Cancel and go back
        </button>
      </template>
    </div>
  </div>
</template>

<style src="../landing/landing.css"></style>

<style scoped>
/* Floats left rather than centering — see `JoinScreen.vue`'s identical note;
   same backdrop, same reason. */
.screen {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  padding: 2rem var(--gutter);
  overflow-y: auto;
  background-color: var(--ink);
  background-size: cover;
  background-position: center;
}

.backdrop-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  opacity: 0;
  transition: opacity 1.1s ease;
}

.backdrop-video.ready {
  opacity: 1;
}

.scrim {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse 60% 50% at 50% 42%,
      color-mix(in srgb, var(--amber) 16%, transparent) 0%,
      transparent 60%
    ),
    radial-gradient(ellipse 90% 80% at 50% 50%, transparent 0%, color-mix(in srgb, var(--ink) 88%, transparent) 78%),
    color-mix(in srgb, var(--ink) 42%, transparent);
}

.panel {
  position: relative;
  z-index: 1;
  width: min(420px, calc(100vw - 2 * var(--gutter)));
  padding: 2.25rem;
  border: 1px solid var(--zinc);
  border-radius: 2px;
  background: color-mix(in srgb, var(--steel) 88%, transparent);
  backdrop-filter: blur(6px);
  box-shadow: 0 30px 70px -20px rgba(0, 0, 0, 0.65);
}

/* Gunsight corners, identical to `JoinScreen.vue`'s — see that file for the
   reasoning on the offset and on the easter egg's colour. */
.bracket {
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  border-style: solid;
  border-width: 0;
  border-color: color-mix(in srgb, var(--frost) 80%, transparent);
  opacity: 0.7;
  pointer-events: none;
  transition: border-color 0.5s ease, opacity 0.5s ease;
}

.bracket-tl {
  top: -24px;
  left: -24px;
  border-top-width: 1.5px;
  border-left-width: 1.5px;
}

.bracket-tr {
  top: -24px;
  right: -24px;
  border-top-width: 1.5px;
  border-right-width: 1.5px;
}

.bracket-bl {
  bottom: -24px;
  left: -24px;
  border-bottom-width: 1.5px;
  border-left-width: 1.5px;
}

.bracket-br {
  bottom: -24px;
  right: -24px;
  border-bottom-width: 1.5px;
  border-right-width: 1.5px;
}

.panel.locked .bracket {
  border-color: #4fe06a;
  opacity: 1;
}

.panel-logo {
  display: block;
  --brand-logo-height: 2rem;
  margin-bottom: 0.5rem;
}

.tagline {
  margin: 0 0 0.4rem;
  font-family: var(--data);
  font-size: 0.72rem;
  font-weight: 500;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--amber);
}

.panel h2 {
  margin: 0 0 0.5rem;
  font-family: var(--display);
  font-variation-settings: 'wdth' 108, 'wght' 700;
  font-size: 1.3rem;
  letter-spacing: 0.04em;
  color: var(--bone);
}

.lede {
  margin: 0 0 1.5rem;
  color: var(--frost);
  font-size: 0.875rem;
  line-height: 1.5;
}

form {
  display: grid;
  gap: 0.4rem;
}

/* See `JoinScreen.vue`'s identical block for why `display: block` and the
   full set of `--altcha-*` names are load-bearing rather than decorative. */
.altcha {
  display: block;
  min-height: 2.6rem;
  margin-top: 0.75rem;
  font-family: var(--data) !important;
  font-size: 0.8rem !important;

  --altcha-max-width: 100%;
  --altcha-padding: 0.55rem 0.7rem;
  --altcha-border-width: 1px;
  --altcha-border-radius: 3px;
  --altcha-border-color: var(--zinc);
  /* Transparent rather than a boxed colour — see `JoinScreen.vue`'s note. */
  --altcha-color-base: transparent;
  --altcha-color-base-content: var(--bone);
  --altcha-color-neutral: var(--zinc);
  --altcha-color-neutral-content: var(--frost);
  --altcha-color-primary: var(--amber);
  --altcha-color-primary-content: #12100c;
  --altcha-color-success: #4fe06a;
  --altcha-color-success-content: #12100c;
  --altcha-color-error: #d9483b;
  --altcha-color-error-content: var(--bone);
  --altcha-checkbox-size: 18px;
  --altcha-checkbox-border-color: var(--zinc);
  --altcha-checkbox-border-radius: 3px;
  --altcha-spinner-color: var(--amber);
  --altcha-transition-duration: 0.4s;
}

/* See `JoinScreen.vue`'s identical rule — the widget is light DOM, so this
   really does reach its logo, which carries no size variable of its own. */
.altcha :deep(.altcha-logo svg) {
  width: 14px;
  height: 14px;
}

/* Set directly on every descendant rather than trusted to inherit down from
   the host — see `JoinScreen.vue`'s identical rule for why. */
.altcha :deep(*) {
  font-family: var(--data) !important;
}

.submit {
  position: relative;
  overflow: hidden;
  width: 100%;
  margin-top: 1rem;
  padding: 0.8rem;
  border: none;
  border-radius: 3px;
  background: var(--zinc);
  color: var(--frost);
  font-family: var(--display);
  font-variation-settings: 'wdth' 106, 'wght' 700;
  font-size: 0.86rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  cursor: not-allowed;
  transition: background-color 0.2s ease, color 0.2s ease;
}

.submit.live {
  background: var(--amber);
  color: #12100c;
  cursor: pointer;
}

.submit.live::after {
  content: '';
  position: absolute;
  top: 0;
  left: -45%;
  width: 45%;
  height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255, 255, 255, 0.65), transparent);
  animation: submit-glare 2.6s ease-in-out infinite;
  pointer-events: none;
}

@keyframes submit-glare {
  from {
    transform: translateX(0%);
  }
  to {
    transform: translateX(320%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .submit.live::after {
    animation: none;
    opacity: 0;
  }
}

.mismatch,
.error {
  margin: -0.15rem 0 0.6rem;
  color: #d9483b;
  font-size: 0.78rem;
}

.error {
  margin: 0.9rem 0 0;
}

.back {
  display: block;
  margin: 1.1rem auto 0;
  padding: 0;
  border: none;
  background: none;
  font-family: var(--data);
  color: var(--frost);
  font-size: 0.78rem;
  letter-spacing: 0.06em;
  cursor: pointer;
}

.back:hover {
  color: var(--bone);
}
</style>
